import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import AppLayout from "@/layouts/AppLayout"
import { Head, useForm, usePage } from "@inertiajs/react"
import { IconArrowLeft } from "@tabler/icons-react"

interface Cycle {
  id: number
  display_name: string
  user_name: string
}

interface ReviewDefaults {
  reviewer_type: string
  reviewer_name: string
  reviewer_email: string
}

interface Props {
  [key: string]: unknown
  cycle: Cycle
  review: ReviewDefaults
}

export default function PerformanceReviewNew() {
  const { cycle, review } = usePage<Props>().props

  const { data, setData, post, processing, errors } = useForm({
    review_type: review.reviewer_type || "",
    reviewer_name: review.reviewer_name || "",
    reviewer_email: review.reviewer_email || "",
  })

  const allErrors = Object.values(errors)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post(`/performance_review_cycles/${cycle.id}/performance_reviews`)
  }

  return (
    <>
      <Head title={`New Review - ${cycle.display_name} - Operations Center`} />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/performance_review_cycles">Performance Review Cycles</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href={`/performance_review_cycles/${cycle.id}`}>{cycle.display_name}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>New Review</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href={`/performance_review_cycles/${cycle.id}`}>
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <div>
              <h1 className="text-lg font-semibold tracking-tight">New Performance Review</h1>
              <p className="text-muted-foreground text-sm">
                {cycle.user_name} - {cycle.display_name}
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-lg">
          <form onSubmit={handleSubmit}>
            <div className="rounded-xl border bg-card">
              <div className="p-6">
                {allErrors.length > 0 && (
                  <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                    <ul className="space-y-1 text-sm text-destructive">
                      {allErrors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="reviewer_type">Reviewer Type</Label>
                    <Select value={data.review_type} onValueChange={(v) => setData("review_type", v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="client">Client</SelectItem>
                        <SelectItem value="peer">Peer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reviewer_name">Reviewer Name</Label>
                    <Input
                      id="reviewer_name"
                      value={data.reviewer_name}
                      onChange={(e) => setData("reviewer_name", e.target.value)}
                      placeholder="Enter reviewer name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reviewer_email">Reviewer Email</Label>
                    <Input
                      id="reviewer_email"
                      type="email"
                      value={data.reviewer_email}
                      onChange={(e) => setData("reviewer_email", e.target.value)}
                      placeholder="reviewer@example.com"
                    />
                  </div>
                </div>
              </div>

              <Separator />
              <div className="flex justify-end gap-2 p-4">
                <Button variant="ghost" asChild>
                  <a href={`/performance_review_cycles/${cycle.id}`}>Cancel</a>
                </Button>
                <Button type="submit" disabled={processing}>
                  {processing ? "Creating..." : "Create Review"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

PerformanceReviewNew.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
